import { useState, useEffect, useCallback, useRef } from 'react'
import { X, Loader2 } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'
import { usePrices } from '@/hooks/use-prices'
import PaymentMethodSelector from '@/components/PaymentMethodSelector'
import PromoCodeInput from '@/components/PromoCodeInput'
import { usePaymentConfig, getPaymentMethodBlockedReason } from '@/hooks/use-payment-config'
import { useToast } from '@/hooks/use-toast'
import type { SelectablePaymentMethod } from '@/types/payment-methods'
import { DEFAULT_PAYMENT_METHOD } from '@/types/payment-methods'
import type { ApplicableDiscount } from '@/types/pos'
import type { GuestType } from '@/constants'
import { useUser } from '@/hooks/use-user'
import { useGetActiveEntryPass } from '@/hooks/use-get-active-entry-pass'
import { apiFetch } from '@/utils'

interface TaxCalculationResult {
	subtotal: number
	fees: Array<{
		type: string
		rate: number
		amount: number
		description: string
	}>
	total: number
}

export interface CheckoutDialogPass {
	id: number
	name: string
	guestType: GuestType
	dayOfWeek: number
	timeSlot: string
	taxCalculation: TaxCalculationResult
	sechPrice: number
	totalSechPrice?: number
}

interface CheckoutDialogProps {
	open: boolean
	onClose: () => void
	onConfirmPayment: (
		selectedMethod: SelectablePaymentMethod,
		appliedDiscount: ApplicableDiscount | null,
	) => void
	pass: CheckoutDialogPass | null
	isProcessing: boolean
}

export default function CheckoutDialog({
	open,
	onClose,
	onConfirmPayment,
	pass,
	isProcessing,
}: CheckoutDialogProps) {
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<SelectablePaymentMethod>(DEFAULT_PAYMENT_METHOD)
	const [appliedDiscount, setAppliedDiscount] = useState<ApplicableDiscount | null>(null)
	const [manualPromoApplied, setManualPromoApplied] = useState(false)
	const [autoApplyDismissed, setAutoApplyDismissed] = useState(false)
	const [discountedTaxCalc, setDiscountedTaxCalc] = useState<TaxCalculationResult | null>(null)
	const [isRecalculatingTax, setIsRecalculatingTax] = useState(false)
	const { convertUsdToToken } = usePrices()
	const { config: paymentConfig } = usePaymentConfig()
	const { toast } = useToast()
	const { uid, walletAddress } = useUser()
	const { activeEntryPasses } = useGetActiveEntryPass(walletAddress)
	const prevPaymentType = useRef(selectedPaymentMethod.type)

	const isCardPayment = selectedPaymentMethod.type === 'CARD'

	// Fetch auto-apply discount for entry pass
	const fetchAutoApplyDiscount = useCallback(async () => {
		if (!pass) return
		try {
			const res = await apiFetch('/v1/pos/discounts/validate', {
				method: 'POST',
				withCredentials: true,
				body: JSON.stringify({
					items: [],
					locationId: 0,
					cartSubtotal: pass.taxCalculation.subtotal,
					customerId: uid,
					transactionType: 'entry_pass',
					entryPassTemplateId: pass.id,
					guestType: pass.guestType,
					dayOfWeek: pass.dayOfWeek,
					timeSlot: pass.timeSlot,
					paymentMethod: selectedPaymentMethod.type,
				}),
			})
			if (!res.ok) return
			const data = await res.json()
			if (data.bestDiscount) {
				setAppliedDiscount(data.bestDiscount)
			} else {
				setAppliedDiscount(null)
			}
		} catch {
			// Silently fail - auto-apply is best-effort
		}
	}, [pass, uid, selectedPaymentMethod.type])

	// Auto-apply discounts when dialog opens with a pass
	useEffect(() => {
		if (!open || !pass || manualPromoApplied || autoApplyDismissed) return
		fetchAutoApplyDiscount()
	}, [open, pass, manualPromoApplied, autoApplyDismissed, fetchAutoApplyDiscount])

	// Re-validate discount when payment method changes
	useEffect(() => {
		const changed = prevPaymentType.current !== selectedPaymentMethod.type
		prevPaymentType.current = selectedPaymentMethod.type
		if (!changed || !open || !pass) return

		// Payment method changed — clear discount state; auto-apply effect will re-validate
		setAppliedDiscount(null)
		setManualPromoApplied(false)
		setAutoApplyDismissed(false)
		setDiscountedTaxCalc(null)
	}, [selectedPaymentMethod.type, open, pass])

	// Reset discount state when pass changes
	useEffect(() => {
		setAppliedDiscount(null)
		setManualPromoApplied(false)
		setAutoApplyDismissed(false)
		setDiscountedTaxCalc(null)
	}, [pass?.id])

	// Handle removing a discount — fallback to auto-apply like the POS does
	const handleRemoveDiscount = useCallback(() => {
		if (appliedDiscount && appliedDiscount.isAutoApply && !manualPromoApplied) {
			// User dismissed an auto-apply discount
			setAutoApplyDismissed(true)
			setAppliedDiscount(null)
		} else {
			// User removed a manual promo — fall back to auto-apply
			setManualPromoApplied(false)
			setAppliedDiscount(null)
			fetchAutoApplyDiscount()
		}
	}, [appliedDiscount, manualPromoApplied, fetchAutoApplyDiscount])

	// Recalculate tax on the discounted subtotal (discount first, then tax)
	useEffect(() => {
		if (!pass || !appliedDiscount) {
			setDiscountedTaxCalc(null)
			return
		}
		const discountedSubtotal = pass.taxCalculation.subtotal - appliedDiscount.calculatedAmount
		if (discountedSubtotal <= 0) {
			setDiscountedTaxCalc({ subtotal: 0, fees: [], total: 0 })
			return
		}
		const recalc = async () => {
			setIsRecalculatingTax(true)
			try {
				const res = await apiFetch('/v1/taxes/calculate', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						amount: discountedSubtotal,
						transactionType: 'entry_pass'
					})
				})
				const result = await res.json()
				if (result.success) {
					setDiscountedTaxCalc(result.data)
				}
			} catch {
				// Fallback: no tax adjustment
				setDiscountedTaxCalc(null)
			} finally {
				setIsRecalculatingTax(false)
			}
		}
		recalc()
	}, [appliedDiscount, pass])

	if (!pass) return null

	const { taxCalculation } = pass

	const formatPrice = (amount: number) => {
		return Number(amount).toFixed(IS_TESTNET ? 6 : 2)
	}

	const discountAmount = appliedDiscount ? appliedDiscount.calculatedAmount : 0
	// Use recalculated tax when discount is applied (discount first, then tax per FL law)
	const effectiveTaxCalc = (appliedDiscount && discountedTaxCalc) ? discountedTaxCalc : taxCalculation

	const finalTotal = effectiveTaxCalc.total

	// Format amount based on selected payment method
	const formatAmount = (usdAmount: number) => {
		if (isCardPayment) {
			return `$${formatPrice(usdAmount)}`
		}
		return `${formatPrice(convertUsdToToken(usdAmount, 'sech'))} SECH`
	}

	return (
		<Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
			<DialogContent className="bg-[#2F2F32] rounded-[16px] w-[92%] max-w-md p-0 border-0">
				{/* Header */}
				<div className="flex items-center justify-between px-5 pt-5 pb-0">
					<h2 className="text-[18px] font-semibold font-figtree text-white">
						Checkout
					</h2>
					<button
						onClick={onClose}
						className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
					>
						<X className="h-4 w-4 text-white" />
					</button>
				</div>

				{/* Pass name */}
				<div className="px-5 pb-0">
					<p className="text-[14px] text-[#BABABB] font-figtree">{pass.name}</p>
				</div>

				{/* Price Breakdown */}
				<div className="px-5">
					<div className="bg-[#18181B] rounded-[12px] p-4">
						<div className="space-y-2 text-sm">
							<div className="flex justify-between">
								<span className="text-[#BABABB]">Subtotal</span>
								<span className="text-[#ffffff]">{formatAmount(taxCalculation.subtotal)}</span>
							</div>
							{appliedDiscount && (
								<div className="flex justify-between text-green-400">
									<span>Discount</span>
									<span>-{formatAmount(discountAmount)}</span>
								</div>
							)}
							<div className="border-t border-dashed border-[#4C4C4C] my-2" />
							{effectiveTaxCalc.fees.map((fee, index) => (
								<div key={index} className="flex justify-between text-[#BABABB]">
									<span>{fee.description} ({(fee.rate * 100).toFixed(1)}%)</span>
									<span>{formatAmount(fee.amount)}</span>
								</div>
							))}
							{isRecalculatingTax && (
								<div className="flex items-center justify-center py-1">
									<Loader2 className="h-3 w-3 animate-spin text-primary mr-1.5" />
									<span className="text-xs text-[#BABABB]">Recalculating tax...</span>
								</div>
							)}
							<div className="flex justify-between font-semibold">
								<span className="text-[#ffffff]">Total</span>
								<span className="text-[#ffffff]">{formatAmount(finalTotal)}</span>
							</div>
						</div>

						{/* Promo Code */}
						<PromoCodeInput
							transactionType="entry_pass"
							subtotal={taxCalculation.subtotal}
							entryPassTemplateId={pass.id}
							guestType={pass.guestType}
							dayOfWeek={pass.dayOfWeek}
							timeSlot={pass.timeSlot}
							paymentMethod={selectedPaymentMethod.type}
							onDiscountChange={(discount) => {
								setAppliedDiscount(discount)
								setManualPromoApplied(!!discount)
							}}
							appliedDiscount={appliedDiscount}
							onRemoveDiscount={handleRemoveDiscount}
						/>
					</div>
				</div>

				{/* Payment Method */}
				<div className="px-5">
					<PaymentMethodSelector
						selectedMethod={selectedPaymentMethod}
						onSelect={setSelectedPaymentMethod}
					/>
				</div>

				{/* Pay Button */}
				<div className="px-5 pb-5">
					<Button
						className="w-full h-[42px] bg-[#EF4444] rounded-[21px] text-white font-semibold shadow-[0px_4px_36px_0px_#DC262699]"
						disabled={isProcessing || isRecalculatingTax || activeEntryPasses.some(p => p.templateId === pass?.id)}
						onClick={() => {
						if (activeEntryPasses.some(p => p.templateId === pass?.id)) {
							onClose()
							return
						}
						const blocked = getPaymentMethodBlockedReason(selectedPaymentMethod.type, paymentConfig)
						if (blocked) {
							toast({ title: 'Payment Method Unavailable', description: blocked, variant: 'destructive' })
							return
						}
						onConfirmPayment(selectedPaymentMethod, appliedDiscount)
					}}
					>
						{isProcessing ? 'Processing...' : activeEntryPasses.some(p => p.templateId === pass?.id) ? 'Pass Already Active' : `Pay ${formatAmount(finalTotal)}`}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
